import React, { useState, useRef, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Alert, AlertDescription } from '../ui/alert';
import {
  Files, Upload, Download, CheckCircle, AlertCircle, RefreshCw, Trash2, Filter, XCircle
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;

const COUNTRY_OPTIONS = [
  "España", "Francia", "Alemania", "Reino Unido", "Italia", "Portugal",
  "Países Bajos", "Bélgica", "Suiza", "Austria", "Irlanda", "Polonia",
  "Rumanía", "Estados Unidos", "Australia", "México", "Argentina",
  "Colombia", "Chile"
];

export const PdfFilterTab = ({ token }) => {
  const [archiveFile, setArchiveFile] = useState(null);
  const [filterMode, setFilterMode] = useState('only'); // 'only' or 'exclude'
  const [selectedCountries, setSelectedCountries] = useState(['España']);
  const [loading, setLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [stage, setStage] = useState('');
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [currentJobId, setCurrentJobId] = useState(null);
  const [jobHistory, setJobHistory] = useState([]);

  const fileInputRef = useRef(null);
  const pollRef = useRef(null);

  const fetchHistory = useCallback(async () => {
    if (!token) return;
    try {
      const res = await axios.get(`${API}/pdf-filter/jobs?limit=5`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setJobHistory(res.data.jobs || []);
    } catch {}
  }, [token]);

  useEffect(() => {
    fetchHistory();
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [fetchHistory]);

  const pollJob = useCallback(async (jobId) => {
    try {
      const res = await axios.get(`${API}/pdf-filter/job/${jobId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const job = res.data;

      if (job.status === 'completed') {
        clearInterval(pollRef.current);
        pollRef.current = null;
        setStage('done');
        setLoading(false);
        setResult(job);
        toast.success(`Filtrado: ${job.kept} PDFs conservados de ${job.total_pdfs}`);
        fetchHistory();
      } else if (job.status === 'error') {
        clearInterval(pollRef.current);
        pollRef.current = null;
        setStage('');
        setLoading(false);
        setError(job.error || 'Error desconocido');
        toast.error('Error en el filtrado');
      }
    } catch {}
  }, [token, fetchHistory]);

  const toggleCountry = (country) => {
    setSelectedCountries(prev =>
      prev.includes(country)
        ? prev.filter(c => c !== country)
        : [...prev, country]
    );
  };

  const CHUNK_SIZE = 4 * 1024 * 1024;

  const handleUpload = async () => {
    if (!archiveFile || selectedCountries.length === 0) {
      setError('Selecciona un archivo y al menos un país');
      return;
    }

    setLoading(true);
    setError('');
    setResult(null);
    setUploadProgress(0);
    setStage('uploading');

    try {
      const jobId = Math.random().toString(36).substring(2, 10);
      setCurrentJobId(jobId);

      // Upload archive in chunks
      const totalChunks = Math.ceil(archiveFile.size / CHUNK_SIZE);
      
      for (let i = 0; i < totalChunks; i++) {
        const start = i * CHUNK_SIZE;
        const end = Math.min(start + CHUNK_SIZE, archiveFile.size);
        const blob = archiveFile.slice(start, end);

        const form = new FormData();
        form.append('job_id', jobId);
        form.append('chunk_index', i.toString());
        form.append('total_chunks', totalChunks.toString());
        form.append('filename', archiveFile.name);
        form.append('filter_mode', filterMode);
        form.append('filter_countries', selectedCountries.join(','));
        form.append('chunk', blob, `chunk_${i}`);

        await axios.post(`${API}/pdf-filter/upload-chunk`, form, {
          headers: {
            'Content-Type': 'multipart/form-data',
            Authorization: `Bearer ${token}`
          },
          timeout: 120000
        });

        setUploadProgress(Math.round(((i + 1) / totalChunks) * 100));
      }

      setStage('processing');
      pollRef.current = setInterval(() => pollJob(jobId), 2000);
    } catch (err) {
      setStage('');
      setLoading(false);
      setError(err.response?.data?.detail || 'Error al subir');
    }
  };

  const handleDownload = async (jobId, type = 'kept') => {
    try {
      const res = await axios.get(`${API}/pdf-filter/job/${jobId}/download?type=${type}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      const labels = { kept: 'conservados', removed: 'eliminados', unknown: 'desconocidos' };
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `${labels[type] || type}_${jobId}.zip`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error('Error al descargar');
    }
  };

  const handleDelete = async (jobId) => {
    try {
      await axios.delete(`${API}/pdf-filter/job/${jobId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Eliminado');
      fetchHistory();
    } catch {}
  };

  const handleReset = () => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    setArchiveFile(null);
    setResult(null);
    setError('');
    setUploadProgress(0);
    setStage('');
    setCurrentJobId(null);
    setLoading(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  return (
    <div className="space-y-4" data-testid="pdf-filter-tab">

      {/* Instructions */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Filter className="h-5 w-5" />
            Filtrar PDFs por País
          </CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-muted-foreground space-y-2">
          <p><strong>1.</strong> Sube un ZIP/RAR con PDFs de facturas</p>
          <p><strong>2.</strong> Elige el modo: "Solo de" o "Quitar los de"</p>
          <p><strong>3.</strong> Selecciona los países</p>
          <p><strong>4.</strong> Descarga el ZIP filtrado</p>
          <p className="text-xs">El país se detecta por la dirección del emisor (no de Purolink)</p>
        </CardContent>
      </Card>

      {/* File Upload */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">1. Archivo</CardTitle>
        </CardHeader>
        <CardContent>
          <input
            type="file"
            ref={fileInputRef}
            onChange={(e) => { setArchiveFile(e.target.files?.[0] || null); setError(''); setResult(null); }}
            accept=".zip,.rar"
            className="hidden"
            data-testid="filter-file-input"
          />
          <div
            onClick={() => !loading && fileInputRef.current?.click()}
            className={`border-2 border-dashed rounded-lg p-4 text-center cursor-pointer transition-colors hover:border-primary/50 ${archiveFile ? 'border-primary bg-primary/5' : ''}`}
          >
            <Files className={`h-8 w-8 mx-auto mb-1 ${archiveFile ? 'text-primary' : 'text-muted-foreground'}`} />
            {archiveFile ? (
              <div>
                <p className="font-medium">{archiveFile.name}</p>
                <p className="text-xs text-muted-foreground">{formatFileSize(archiveFile.size)}</p>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">Click para seleccionar ZIP/RAR</p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Filter Config */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">2. Filtro</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {/* Mode */}
          <div className="flex gap-2">
            <Button
              variant={filterMode === 'only' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setFilterMode('only')}
              data-testid="filter-mode-only"
            >
              Solo de...
            </Button>
            <Button
              variant={filterMode === 'exclude' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setFilterMode('exclude')}
              data-testid="filter-mode-exclude"
            >
              Quitar los de...
            </Button>
          </div>

          {/* Country chips */}
          <div className="flex flex-wrap gap-1.5">
            {COUNTRY_OPTIONS.map(country => (
              <button
                key={country}
                onClick={() => toggleCountry(country)}
                className={`px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${
                  selectedCountries.includes(country)
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-muted text-muted-foreground hover:bg-muted/80'
                }`}
                data-testid={`country-chip-${country}`}
              >
                {country}
              </button>
            ))}
          </div>

          <p className="text-xs text-muted-foreground">
            {filterMode === 'only'
              ? `Conservar solo PDFs de: ${selectedCountries.join(', ')}`
              : `Eliminar PDFs de: ${selectedCountries.join(', ')}`
            }
          </p>
        </CardContent>
      </Card>

      {/* Actions */}
      <div className="flex gap-2">
        <Button
          onClick={handleUpload}
          disabled={!archiveFile || selectedCountries.length === 0 || loading}
          className="flex-1"
          data-testid="filter-process-button"
        >
          {loading ? <RefreshCw className="h-4 w-4 animate-spin mr-2" /> : <Filter className="h-4 w-4 mr-2" />}
          Filtrar
        </Button>
        {archiveFile && (
          <Button variant="outline" onClick={handleReset} disabled={loading} data-testid="filter-reset-button">
            <XCircle className="h-4 w-4" />
          </Button>
        )}
      </div>

      {error && (
        <Alert variant="destructive"><AlertCircle className="h-4 w-4" /><AlertDescription>{error}</AlertDescription></Alert>
      )}

      {/* Progress */}
      {loading && (
        <Card>
          <CardContent className="pt-4">
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">
                  {stage === 'uploading' && 'Subiendo...'}
                  {stage === 'processing' && 'Analizando PDFs y filtrando por país...'}
                </span>
                {stage === 'uploading' && <span className="font-medium">{uploadProgress}%</span>}
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className={`h-2 rounded-full transition-all ${stage === 'processing' ? 'bg-yellow-500 animate-pulse w-full' : 'bg-primary'}`}
                  style={{ width: stage === 'processing' ? '100%' : `${uploadProgress}%` }}
                />
              </div>
              {stage === 'processing' && (
                <div className="flex items-center gap-2 text-yellow-600 text-sm">
                  <RefreshCw className="h-4 w-4 animate-spin" />
                  <span>Leyendo cada PDF y detectando país del emisor...</span>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Results */}
      {result && (
        <Card className="border-green-200 bg-green-50/50">
          <CardContent className="pt-6 space-y-3">
            <div className="flex items-center gap-2 text-green-700 font-semibold text-lg">
              <CheckCircle className="h-5 w-5" />
              Filtrado completado
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div className="bg-white rounded-lg p-3 text-center border" data-testid="filter-stat-total">
                <p className="text-2xl font-bold text-blue-600">{result.total_pdfs}</p>
                <p className="text-xs text-muted-foreground">Analizados</p>
              </div>
              <div className="bg-white rounded-lg p-3 text-center border" data-testid="filter-stat-kept">
                <p className="text-2xl font-bold text-green-600">{result.kept}</p>
                <p className="text-xs text-muted-foreground">Conservados</p>
              </div>
              <div className="bg-white rounded-lg p-3 text-center border" data-testid="filter-stat-removed">
                <p className="text-2xl font-bold text-red-600">{result.removed}</p>
                <p className="text-xs text-muted-foreground">Eliminados</p>
              </div>
              <div className="bg-white rounded-lg p-3 text-center border" data-testid="filter-stat-unknown">
                <p className="text-2xl font-bold text-yellow-600">{result.unknown || 0}</p>
                <p className="text-xs text-muted-foreground">Desconocidos</p>
              </div>
            </div>

            {/* By country breakdown */}
            {result.by_country && Object.keys(result.by_country).length > 0 && (
              <div className="space-y-1">
                <p className="text-sm font-medium">Por país:</p>
                <div className="flex flex-wrap gap-2">
                  {Object.entries(result.by_country).sort((a,b) => b[1] - a[1]).map(([country, count]) => {
                    const isKept = filterMode === 'only'
                      ? selectedCountries.includes(country)
                      : !selectedCountries.includes(country);
                    const isUnknown = country === 'Desconocido';
                    return (
                      <span
                        key={country}
                        className={`text-xs px-2 py-1 rounded-full ${
                          isUnknown ? 'bg-yellow-100 text-yellow-700' :
                          isKept ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                        }`}
                      >
                        {country}: {count}
                      </span>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Download buttons */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              {result.kept > 0 && (
                <Button onClick={() => handleDownload(result.id || currentJobId, 'kept')} variant="default" className="bg-green-600 hover:bg-green-700" data-testid="filter-download-kept">
                  <Download className="h-4 w-4 mr-2" />
                  Conservados ({result.kept})
                </Button>
              )}
              {result.removed > 0 && (
                <Button onClick={() => handleDownload(result.id || currentJobId, 'removed')} variant="default" className="bg-red-600 hover:bg-red-700" data-testid="filter-download-removed">
                  <Download className="h-4 w-4 mr-2" />
                  Eliminados ({result.removed})
                </Button>
              )}
              {(result.unknown || 0) > 0 && (
                <Button onClick={() => handleDownload(result.id || currentJobId, 'unknown')} variant="default" className="bg-yellow-600 hover:bg-yellow-700" data-testid="filter-download-unknown">
                  <Download className="h-4 w-4 mr-2" />
                  Desconocidos ({result.unknown})
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* History */}
      {jobHistory.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Historial</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {jobHistory.map(job => (
              <div key={job.id} className="flex items-center justify-between bg-muted/50 rounded-lg px-3 py-2">
                <div>
                  <p className="text-sm font-medium">
                    {job.total_pdfs} PDFs → {job.kept} conservados
                    <span className="text-muted-foreground ml-1">
                      ({job.filter_mode === 'only' ? 'Solo' : 'Sin'} {(job.filter_countries || []).join(', ')})
                    </span>
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {new Date(job.created_at).toLocaleString('es-ES')}
                  </p>
                </div>
                <div className="flex gap-1">
                  {job.status === 'completed' && (
                    <>
                      {(job.kept || 0) > 0 && (
                        <Button variant="ghost" size="sm" onClick={() => handleDownload(job.id, 'kept')} title="Conservados">
                          <Download className="h-4 w-4 text-green-600" />
                        </Button>
                      )}
                      {(job.removed || 0) > 0 && (
                        <Button variant="ghost" size="sm" onClick={() => handleDownload(job.id, 'removed')} title="Eliminados">
                          <Download className="h-4 w-4 text-red-600" />
                        </Button>
                      )}
                    </>
                  )}
                  <Button variant="ghost" size="sm" onClick={() => handleDelete(job.id)}>
                    <Trash2 className="h-4 w-4 text-red-500" />
                  </Button>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default PdfFilterTab;
